import React, { memo, useEffect, useState } from "react";
import { Box, Tooltip, Icon, Flex, Text } from "@chakra-ui/react";
import { FaRss, FaNewspaper, FaListAlt, FaClock, FaChartBar, FaBook } from "react-icons/fa";
import Bar from "../layout/Bar";
import useArticleStore from "../../stores/useArticleStore";
import useFeedStore from "../../stores/useFeedStore";

const InfoBar = memo(() => {
  const { articles } = useArticleStore();
  const { feeds, categories } = useFeedStore();
  const [lastUpdate, setLastUpdate] = useState(null);
  const [tickerIndex, setTickerIndex] = useState(0);
  const [categoryStats, setCategoryStats] = useState([]);

  useEffect(() => {
    // If there are articles, set last update time to current time
    if (articles.length > 0) {
      setLastUpdate(new Date());
    }
  }, [articles]);

  // Count articles by category
  const articlesByCategory = {};
  articles.forEach(article => {
    const category = article.category || 'Uncategorized';
    if (!articlesByCategory[category]) {
      articlesByCategory[category] = 0;
    }
    articlesByCategory[category]++;
  });

  // Handle InfoTicker statistics data
  useEffect(() => {
    if (articles.length > 0 && categories.length > 0) {
      const stats = [];

      // Count by category
      const categoryMap = {};
      articles.forEach(article => {
        const category = article.category || 'Uncategorized';
        if (!categoryMap[category]) {
          categoryMap[category] = 0;
        }
        categoryMap[category]++;
      });

      // Convert to array format
      for (const category of categories) {
        if (category) {
          stats.push({
            icon: FaBook,
            label: category,
            value: `${categoryMap[category] || 0} articles`
          });
        }
      }

      // Add average articles per category
      const avgPerCategory = categories.length > 0 ? Math.round(articles.length / categories.length) : 0;
      stats.push({
        icon: FaChartBar,
        label: "Avg per Category",
        value: `${avgPerCategory} articles`
      });

      setCategoryStats(stats);
    }
  }, [articles, categories]);

  // Auto carousel effect
  useEffect(() => {
    const interval = setInterval(() => {
      if (categoryStats.length > 0) {
        setTickerIndex(prevIndex => (prevIndex + 1) % categoryStats.length);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [categoryStats]);

  // Format date time
  const formatDateTime = (date) => {
    if (!date) return "Not updated yet";
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get current category statistics to display
  const currentStat = categoryStats[tickerIndex] || { icon: FaBook, label: 'Loading', value: '...' };

  return (
    <Bar>
      <Flex width="100%" alignItems="center" justifyContent="space-between">
        <Flex>
          <Tooltip label="Total Articles">
            <Box mr={4} display="flex" alignItems="center">
              <Icon as={FaNewspaper} mr={2} />
              <Box display="inline">
                <Text as="span" fontWeight="bold">Articles: </Text>
                <Text as="span">{articles.length}</Text>
              </Box>
            </Box>
          </Tooltip>

          <Tooltip label="Total Feeds">
            <Box mr={4} display="flex" alignItems="center">
              <Icon as={FaRss} mr={2} />
              <Box display="inline">
                <Text as="span" fontWeight="bold">Feeds: </Text>
                <Text as="span">{feeds.length}</Text>
              </Box>
            </Box>
          </Tooltip>

          <Tooltip label="Total Categories">
            <Box mr={4} display="flex" alignItems="center">
              <Icon as={FaListAlt} mr={2} />
              <Box display="inline">
                <Text as="span" fontWeight="bold">Categories: </Text>
                <Text as="span">{categories.length}</Text>
              </Box>
            </Box>
          </Tooltip>

          <Tooltip label="Last Update Time">
            <Box mr={4} display="flex" alignItems="center">
              <Icon as={FaClock} mr={2} />
              <Box display="inline">
                <Text as="span" fontWeight="bold">Updated: </Text>
                <Text as="span">{formatDateTime(lastUpdate)}</Text>
              </Box>
            </Box>
          </Tooltip>
        </Flex>

        {/* Right side carousel statistics */}
        <Flex alignItems="center">
          <Icon as={currentStat.icon} mr={2} />
          <Box display="inline">
            <Text as="span" fontWeight="bold">{currentStat.label}: </Text>
            <Text as="span">{currentStat.value}</Text>
          </Box>
        </Flex>
      </Flex>
    </Bar>
  );
});

export default InfoBar;
